import React, { useState, useRef, useEffect } from 'react';
import './Card.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faVrCardboard, faGamepad } from '@fortawesome/free-solid-svg-icons';
import { faSteam, faYoutube, faItchIo, faGithub, faUnity } from '@fortawesome/free-brands-svg-icons';

const PLATFORM_CONFIG = {
    steam:   { icon: faSteam,       label: 'Steam',             color: '#c6d4df' },
    itch:    { icon: faItchIo,      label: 'itch.io',           color: '#fa5c5c' },
    web:     { icon: faGlobe,       label: 'Web',               color: '#5ba4cf' },
    youtube: { icon: faYoutube,     label: 'YouTube',           color: '#ff4444' },
    quest:   { icon: faVrCardboard, label: 'Meta Quest',        color: '#0064e0' },
    vr:      { icon: faVrCardboard, label: 'VR',                color: '#9b87d7' },
    unity:   { icon: faUnity,       label: 'Unity Asset Store', color: '#d4d4d4' },
    switch:  { icon: faGamepad,     label: 'Nintendo Switch',   color: '#e60012' },
    pico:    { icon: faVrCardboard, label: 'Pico',              color: '#00b4ff' },
    htc:     { icon: faVrCardboard, label: 'HTC Vive',          color: '#8bbfd4' },
    github:  { icon: faGithub,      label: 'GitHub',            color: '#d4d4d4' },
};

const isVideoSrc = (src) => src && /\.(mp4|webm|ogg)$/i.test(src);

const Card = ({ image, hoverImage, title, description, url, links = [], onClick, imagePosition = 'center' }) => {
    const [isHovered, setIsHovered] = useState(false);
    const videoRef = useRef(null);
    const primaryUrl = url || (links.length > 0 ? links[0].url : '#');
    const hoverIsVideo = isVideoSrc(hoverImage);

    useEffect(() => {
        if (!videoRef.current) return;
        if (isHovered) {
            videoRef.current.play().catch(() => {});
        } else {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    }, [isHovered]);

    return (
        <div
            className={`project-row ${isHovered ? 'project-row--hovered' : ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <a
                className="project-row__overlay"
                href={primaryUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                aria-label={title}
            />
            <div className="project-row__image">
                <img
                    src={(!hoverIsVideo && isHovered && hoverImage) ? hoverImage : image}
                    alt={title}
                    style={{ objectPosition: imagePosition }}
                    className={hoverIsVideo && isHovered ? 'project-row__media--hidden' : ''}
                />
                {hoverIsVideo && (
                    <video
                        ref={videoRef}
                        className={`project-row__hover-video${isHovered ? ' project-row__hover-video--active' : ''}`}
                        src={hoverImage}
                        muted
                        loop
                        playsInline
                    />
                )}
            </div>
            <div className="project-row__content">
                <h2 className="project-row__title">{title}</h2>
                <p className="project-row__description">{description}</p>
                {links.length > 0 && (
                    <div className="project-row__links">
                        {links.map(({ platform, url: linkUrl }) => {
                            const config = PLATFORM_CONFIG[platform] ?? { icon: faGlobe, label: platform, color: '#aaa' };
                            return (
                                <a
                                    key={platform}
                                    className="project-row__link-btn"
                                    href={linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ '--link-color': config.color }}
                                    onClick={onClick}
                                >
                                    <FontAwesomeIcon icon={config.icon} />
                                    <span>{config.label}</span>
                                </a>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Card;
